import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { buildSeatMap } from './seatingMoves';

/**
 * Seating Chart Designer (host 2026-07-13) — the printable "Seated Roster" becomes a live
 * design surface, so the host never has to take a PDF to an outside tool again.
 *
 * WYSIWYG contract: the preview iframe and the print tab are rendered from the SAME
 * buildChartDoc() HTML — what you see is exactly what prints.
 *
 * Everything the host asked for:
 *   • sort each table by first name / last name / seat order (families together)
 *   • 1–3 columns, font pairing (the app's own Google faces), size scale
 *   • background color or an uploaded picture with a readability (lightness) control
 *   • per-DOCUMENT table display names ("Table 1" → "Carol's Table") — the operational
 *     table number is untouched; permanent renames live on the table card (✎)
 *   • fully editable title + subtitle lines
 *   • the design persists with the event (plan.settings.chart_design)
 */

export const CHART_FONTS = [
  ['playfair', 'Playfair Display', "'Playfair Display',Georgia,serif", 'Helvetica,Arial,sans-serif'],
  ['cormorant', 'Cormorant Garamond', "'Cormorant Garamond',Georgia,serif", "'Cormorant Garamond',Georgia,serif"],
  ['cinzel', 'Cinzel', "'Cinzel',Georgia,serif", 'Helvetica,Arial,sans-serif'],
  ['script', 'Great Vibes', "'Great Vibes',cursive", "'Cormorant Garamond',Georgia,serif"],
  ['classic', 'Georgia', "Georgia,'Times New Roman',serif", 'Helvetica,Arial,sans-serif'],
];
const FONT_LINK = '<link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600&family=Playfair+Display:wght@400;500;600&family=Cormorant+Garamond:wght@400;500;600&family=Great+Vibes&display=swap" rel="stylesheet">';

export const DEFAULT_CHART_DESIGN = {
  title: 'Seated Roster',
  subtitle: '',            // '' = the auto line ("OUR CELEBRATION · 9 TABLES · 95 SEATED")
  sort: 'first',           // first | last | seat
  columns: 3,
  font: 'playfair',
  scale: 1,                // 0.8–1.4 multiplier on all text
  bg: '#ffffff',
  bg_image: null,          // data-URL picture behind the chart
  bg_lightness: 0.86,      // white veil over the picture so names stay readable
  show_counts: true,       // the small 10/12 per table
  table_names: {},         // per-document display names, keyed by table id
  table_order: [],         // per-document print order (table ids) — drag ⠿ in the panel
};

/** Document print order (host 2026-07-21): tables the host dragged into place first
    (in that order), any others after in their natural order. Stable sort. */
export function orderTablesForDoc(tables, order) {
  const pos = new Map((Array.isArray(order) ? order : []).map((id, i) => [id, i]));
  return [...(tables || [])].sort((a, b) =>
    (pos.has(a.id) ? pos.get(a.id) : Infinity) - (pos.has(b.id) ? pos.get(b.id) : Infinity) || 0);
}

function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
const firstName = (n) => (String(n || '').trim().split(/\s+/)[0] || '').toLowerCase();
const lastName = (n) => { const p = String(n || '').trim().split(/\s+/); return (p[p.length - 1] || '').toLowerCase(); };

/** Per-table entries [{gid, name, seat}] in the chosen order — shared with the Export prints. */
export function orderedEntries(table, assignments, byId, seatMap, mode) {
  const arr = (seatMap && seatMap[table.id]) || [];
  const entries = [];
  arr.forEach((gid, i) => { if (gid) entries.push({ gid, name: (byId[gid] || {}).name || 'Guest', seat: i + 1 }); });
  if (mode === 'last') entries.sort((a, b) => lastName(a.name).localeCompare(lastName(b.name)) || firstName(a.name).localeCompare(firstName(b.name)));
  else if (mode === 'first') entries.sort((a, b) => firstName(a.name).localeCompare(firstName(b.name)) || lastName(a.name).localeCompare(lastName(b.name)));
  /* mode==='seat' keeps chair order — families sit (and print) together */
  return entries;
}

/** The ONE chart document — used verbatim for the live preview AND the print tab. */
export function buildChartDoc(design, { tables, assignments, byId, planName }, extraTopBar = '') {
  const d = { ...DEFAULT_CHART_DESIGN, ...(design || {}) };
  const seatMap = buildSeatMap(tables, assignments);
  const seatedCount = (assignments || []).filter((a) => a.table_id).length;
  const font = CHART_FONTS.find((f) => f[0] === d.font) || CHART_FONTS[0];
  const sub = (d.subtitle || '').trim()
    ? d.subtitle
    : `${planName || 'Our Celebration'} · ${tables.length} tables · ${seatedCount} seated`;
  const sections = orderTablesForDoc(tables, d.table_order).map((t) => {
    const label = (d.table_names || {})[t.id] || t.label || 'Table';
    const entries = orderedEntries(t, assignments, byId, seatMap, d.sort);
    const items = entries.map((e) => (d.sort === 'seat'
      ? `<li><span class="sn">${e.seat}</span>${esc(e.name)}</li>`
      : `<li>${esc(e.name)}</li>`)).join('') || '<li class="e">—</li>';
    const count = d.show_counts ? `<span>${entries.length}/${t.seat_count || 8}</span>` : '';
    return `<div class="rt"><h3>${esc(label)} ${count}</h3><${d.sort === 'seat' ? 'ul' : 'ol'}>${items}</${d.sort === 'seat' ? 'ul' : 'ol'}></div>`;
  }).join('');
  const s = Number(d.scale) || 1;
  const bgLayers = d.bg_image
    ? `background-image:linear-gradient(rgba(255,255,255,${d.bg_lightness}),rgba(255,255,255,${d.bg_lightness})),url('${d.bg_image}');background-size:cover;background-position:center;background-attachment:fixed;`
    : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(d.title || 'Seating Chart')}</title>${FONT_LINK}<style>
    @page{margin:.5in}
    html{background:${d.bg};${bgLayers}}
    body{font-family:${font[3]};color:#2A2622;margin:24px;background:transparent;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    h1{font-family:${font[2]};font-weight:500;font-size:${Math.round(30 * s)}px;margin:0 0 2px}
    .sub{font-size:${Math.round(10 * s)}px;letter-spacing:.2em;text-transform:uppercase;color:#9C7B31;margin-bottom:${Math.round(16 * s)}px}
    /* grid (not newspaper columns) so every row of tables top-aligns — the rule
       under each table name sits level across the page (host 2026-07-21) */
    .roster{display:grid;grid-template-columns:repeat(${d.columns},1fr);column-gap:26px;row-gap:${Math.round(14 * s)}px;align-items:start}
    .rt{break-inside:avoid}
    h3{font-family:${font[2]};font-size:${Math.round(15 * s)}px;margin:0 0 4px;border-bottom:1px solid rgba(42,38,34,.14);padding-bottom:3px}
    h3 span{float:right;font-family:Helvetica,Arial,sans-serif;font-size:${Math.round(10 * s)}px;color:#9C7B31}
    ol,ul{margin:0;padding-left:${Math.round(18 * s)}px} ul{list-style:none;padding-left:4px}
    li{font-size:${Math.round(11.5 * s)}px;padding:1px 0} li.e{list-style:none;opacity:.5}
    .sn{display:inline-block;min-width:${Math.round(16 * s)}px;color:#9C7B31;font-family:Helvetica,Arial,sans-serif;font-size:${Math.round(9.5 * s)}px}
    ${extraTopBar ? '@media screen{body{padding-top:54px}}@media print{.noprint{display:none!important}}.noprint{position:fixed;top:0;left:0;right:0;z-index:99;display:flex;gap:10px;align-items:center;padding:9px 16px;background:#fff;border-bottom:1px solid #ececec;font-family:Helvetica,Arial,sans-serif}.noprint .msg{margin-right:auto;font-size:12px;color:#988F80}.noprint .nb{font-size:13px;border:1px solid #c9c9d0;background:#fff;border-radius:8px;padding:7px 13px;cursor:pointer}.noprint .nb.g{background:linear-gradient(180deg,#d8bd74,#c2a14f);border-color:transparent;font-weight:700;color:#3a2f12}' : ''}
  </style></head><body>${extraTopBar}
    <h1>${esc(d.title || '')}</h1><div class="sub">${esc(sub)}</div>
    <div class="roster">${sections}</div>
  </body></html>`;
}

const SORTS = [['first', 'First name A–Z'], ['last', 'Last name A–Z'], ['seat', 'Seat order (families together)']];

export default function ChartDesigner({ open, onClose, plan, poolList, onSaveDesign }) {
  const tables = useMemo(() => (plan && plan.tables) || [], [plan]);
  const assignments = useMemo(() => (plan && plan.assignments) || [], [plan]);
  const planName = plan && plan.name;
  const saved = (plan && plan.settings && plan.settings.chart_design) || {};
  const [d, setD] = useState({ ...DEFAULT_CHART_DESIGN, ...saved });
  const [savedTick, setSavedTick] = useState(false);
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const dragFrom = useRef(null);   // index being dragged in the table-order list

  useEffect(() => { if (open) { setD({ ...DEFAULT_CHART_DESIGN, ...((plan && plan.settings && plan.settings.chart_design) || {}) }); dirtyRef.current = false; } },
    [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  const byId = useMemo(() => { const m = {}; (poolList || []).forEach((g) => { m[g.id] = g; }); return m; }, [poolList]);
  const doc = useMemo(() => buildChartDoc(d, { tables, assignments, byId, planName }), [d, tables, assignments, byId, planName]);

  // the design saves with the event — debounced so typing doesn't spam the plan endpoint
  const set = (patch) => {
    setD((prev) => {
      const next = { ...prev, ...patch };
      dirtyRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        try { await onSaveDesign(next); dirtyRef.current = false; setSavedTick(true); setTimeout(() => setSavedTick(false), 1600); } catch (e) { /* keep editing; print still works */ }
      }, 900);
      return next;
    });
  };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const printNow = () => {
    const bar = '<div class="noprint"><span class="msg">Opened in a new tab — your seating page is still open behind this.</span><button class="nb g" onclick="window.print()">🖨 Print / Save as PDF</button><button class="nb" onclick="window.close()">✕ Close &amp; go back</button></div>';
    const win = window.open('', '_blank'); if (!win) return;
    win.document.write(buildChartDoc(d, { tables, assignments, byId, planName }, bar));
    win.document.close(); win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { /* */ } }, 400);
  };

  const pickBg = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1800; const k = Math.min(1, MAX / Math.max(img.width, img.height));
        const c = document.createElement('canvas'); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        set({ bg_image: c.toDataURL('image/jpeg', 0.82) });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  if (!open) return null;
  // Portal to <body>: the admin shell's <main> creates its own stacking context, which would
  // trap this fixed overlay UNDER the fixed sidebar/header no matter the z-index.
  return createPortal(
    <div className="as-cd" role="dialog" aria-modal="true" aria-label="Seating Chart Designer">
      <div className="as-cdbar">
        <div className="as-cdtitle">Seating Chart Designer</div>
        <span className={`as-cdsaved ${savedTick ? 'on' : ''}`}>{savedTick ? '✓ Design saved with your event' : (dirtyRef.current ? 'Saving…' : '')}</span>
        <button className="as-btn gold" onClick={printNow}>🖨 Print / Save as PDF</button>
        <button className="as-btn" onClick={onClose}>✕ Close</button>
      </div>
      <div className="as-cdbody">
        <div className="as-cdpanel">
          <div className="as-cdlab">Title &amp; subtitle — click and type</div>
          <input className="as-cdinput" value={d.title} onChange={(e) => set({ title: e.target.value })} placeholder="Seated Roster" aria-label="Chart title" />
          <input className="as-cdinput" value={d.subtitle} onChange={(e) => set({ subtitle: e.target.value })}
            placeholder={`${planName || 'Our Celebration'} · ${tables.length} tables · ${assignments.filter((a) => a.table_id).length} seated (auto)`} aria-label="Chart subtitle" />
          <div className="as-cdhint">Leave the subtitle empty to keep the automatic line.</div>

          <div className="as-cdlab">Sort each table by</div>
          <div className="as-chiprow">{SORTS.map(([k, l]) => <span key={k} className={`as-pick ${d.sort === k ? 'on' : ''}`} onClick={() => set({ sort: k })}>{l}</span>)}</div>

          <div className="as-cdlab">Columns</div>
          <div className="as-chiprow">{[1, 2, 3].map((n) => <span key={n} className={`as-pick ${d.columns === n ? 'on' : ''}`} onClick={() => set({ columns: n })}>{n}</span>)}</div>

          <div className="as-cdlab">Font</div>
          <div className="as-chiprow">{CHART_FONTS.map(([k, name, stack]) => <span key={k} className={`as-pick ${d.font === k ? 'on' : ''}`} style={{ fontFamily: stack }} onClick={() => set({ font: k })}>{name}</span>)}</div>

          <div className="as-cdlab">Text size</div>
          <input type="range" min="0.8" max="1.4" step="0.05" value={d.scale} onChange={(e) => set({ scale: Number(e.target.value) })} aria-label="Text size" />

          <div className="as-cdlab">Background</div>
          <div className="as-chiprow" style={{ alignItems: 'center' }}>
            {[['#ffffff', 'White'], ['#FBF7EE', 'Ivory'], ['#F4EFE3', 'Champagne']].map(([c, l]) => (
              <span key={c} className={`as-pick ${(!d.bg_image && d.bg === c) ? 'on' : ''}`} onClick={() => set({ bg: c, bg_image: null })}>{l}</span>
            ))}
            <input type="color" value={d.bg} onChange={(e) => set({ bg: e.target.value, bg_image: null })} title="Custom color" aria-label="Custom background color" style={{ width: 34, height: 28, border: 'none', background: 'none', cursor: 'pointer' }} />
            <label className="as-pick" style={{ cursor: 'pointer' }}>
              🖼 Picture…
              <input type="file" accept="image/*" hidden onChange={(e) => { pickBg(e.target.files && e.target.files[0]); e.target.value = ''; }} />
            </label>
            {d.bg_image && <span className="as-pick" onClick={() => set({ bg_image: null })}>✕ Remove picture</span>}
          </div>
          {d.bg_image && (
            <>
              <div className="as-cdlab">Picture lightness (keeps names readable)</div>
              <input type="range" min="0.5" max="0.97" step="0.01" value={d.bg_lightness} onChange={(e) => set({ bg_lightness: Number(e.target.value) })} aria-label="Background picture lightness" />
            </>
          )}

          <div className="as-cdlab">
            <label style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={!!d.show_counts} onChange={(e) => set({ show_counts: e.target.checked })} /> Show seat counts (10/12)
            </label>
          </div>

          <div className="as-cdlab">Table order &amp; names on this document</div>
          <div className="as-cdhint">Drag ⠿ to set the order the tables print in (top of this list prints first). Display names here are for this chart only — the table’s real name (used by guest lookup and cards) is edited with ✎ on its table card; guests always stay with their real table.</div>
          <div className="as-cdtables">
            {orderTablesForDoc(tables, d.table_order).map((t, i, arr) => (
              <div key={t.id} className="as-cdtrow" draggable
                onDragStart={(e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragFrom.current; dragFrom.current = null;
                  if (from == null || from === i) return;
                  const ids = arr.map((x) => x.id);
                  const [moved] = ids.splice(from, 1); ids.splice(i, 0, moved);
                  set({ table_order: ids });
                }}>
                <span className="as-cddrag" title="Drag to reorder" aria-hidden="true">⠿</span>
                <span className="as-cdtorig">{t.label || 'Table'}</span>
                <input className="as-cdinput sm" value={(d.table_names || {})[t.id] || ''} placeholder={t.label || 'Table'}
                  onChange={(e) => { const tn = { ...(d.table_names || {}) }; if (e.target.value) tn[t.id] = e.target.value; else delete tn[t.id]; set({ table_names: tn }); }}
                  aria-label={`Display name for ${t.label || 'table'}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="as-cdpreview">
          <iframe title="Seating chart preview — exactly what prints" srcDoc={doc} />
        </div>
      </div>
    </div>,
    document.body
  );
}
